import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "./api";

// Canonical 8-currency default. Used both as an initial-render placeholder AND as a
// safety fallback if `/api/fx-rates` returns an empty payload (e.g. settings wiped,
// backend hiccup, or the CDN caches an empty response for a stale entry). This
// guarantees the header dropdown ALWAYS has 8 pickable options.
const DEFAULT_RATES = {
  GBP: { symbol: "£",   rate: 1.0,   name: "British Pound",     locale: "en-GB" },
  USD: { symbol: "$",   rate: 1.26,  name: "US Dollar",         locale: "en-US" },
  EUR: { symbol: "€",   rate: 1.18,  name: "Euro",              locale: "en-IE" },
  CAD: { symbol: "CA$", rate: 1.72,  name: "Canadian Dollar",   locale: "en-CA" },
  AUD: { symbol: "A$",  rate: 1.92,  name: "Australian Dollar", locale: "en-AU" },
  INR: { symbol: "₹",   rate: 105.0, name: "Indian Rupee",      locale: "en-IN" },
  JPY: { symbol: "¥",   rate: 190.0, name: "Japanese Yen",      locale: "ja-JP" },
  SGD: { symbol: "S$",  rate: 1.68,  name: "Singapore Dollar",  locale: "en-SG" },
};

const CurrencyCtx = createContext({
  code: "GBP", rates: DEFAULT_RATES, setCode: () => {},
  format: (v) => `£${(v||0).toFixed(2)}`,
});

export function CurrencyProvider({ children }) {
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [code, setCodeState] = useState(localStorage.getItem("av_currency") || "GBP");

  const fetchRates = useCallback(async () => {
    try {
      const r = await api.get("/fx-rates");
      const live = r?.data?.currencies || {};
      if (Object.keys(live).length >= 2) {
        setRates(live);
      } else {
        // Server returned empty/broken — keep defaults so dropdown never goes empty.
        setRates(DEFAULT_RATES);
      }
    } catch {
      setRates(DEFAULT_RATES);
    }
  }, []);

  // Fetch once on mount AND once again after auth state settles (many auth setups race
  // the first /fx-rates call with the `/auth/me` bootstrap; a second call keeps the
  // dropdown populated for logged-in users too).
  useEffect(() => { fetchRates(); }, [fetchRates]);
  useEffect(() => {
    const iv = setTimeout(fetchRates, 800);
    return () => clearTimeout(iv);
  }, [fetchRates]);

  const setCode = useCallback((c) => { localStorage.setItem("av_currency", c); setCodeState(c); }, []);

  const format = useCallback((gbp, opts = {}) => {
    const info = rates[code] || rates.GBP || { symbol: "£", rate: 1, locale: "en-GB" };
    const converted = (gbp || 0) * (info.rate || 1);
    const isJPY = code === "JPY";
    const isGBP = code === "GBP";
    let formatted;
    try {
      formatted = new Intl.NumberFormat(info.locale || "en-GB", {
        style: "currency", currency: code,
        minimumFractionDigits: isJPY ? 0 : (opts.min ?? 2),
        maximumFractionDigits: isJPY ? 0 : (opts.max ?? 2),
      }).format(converted);
    } catch {
      const digits = isJPY ? 0 : 2;
      formatted = `${info.symbol}${converted.toFixed(digits)}`;
    }
    // Prefix non-GBP with ≈ so customers immediately see this is an
    // approximate conversion — Stripe charges the exact GBP amount, and
    // their bank's rate on the day determines the real debit. Callers can
    // opt out with `{ raw: true }` where the ≈ would be visually noisy
    // (e.g. inline tabular sums that already have a full "approx" caption).
    if (!isGBP && !opts.raw) {
      return `≈\u00A0${formatted}`;
    }
    return formatted;
  }, [rates, code]);

  const isBase = code === "GBP";

  const value = useMemo(
    () => ({ code, rates, setCode, format, isBase }),
    [code, rates, setCode, format, isBase],
  );

  return (
    <CurrencyCtx.Provider value={value}>
      {children}
    </CurrencyCtx.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyCtx);
